import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BrainCircuit, Search, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/components/ConfirmDialog";

/** Tedarikçi ürün kodu hafızası — hangi tedarikçi kodu hangi stok ürününe bağlandı (onaylarla otomatik öğrenilir). */
export default function SupplierCodesDialog({ open, onClose }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get(`/stock/supplier-codes${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`).then((r) => setRows(r.data)).catch((e) => { toast.error(apiError(e)); setRows([]); });
  useEffect(() => { if (open) { setRows(null); load(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!open) return; const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  const del = async (r) => {
    if (!await confirm(`"${r.code}" kodunun ${r.product_name} eşleşmesi hafızadan silinsin mi?`)) return;
    try { await api.delete(`/stock/supplier-codes/${r.id}`); toast.success("Silindi"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[96vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="supplier-codes-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-2xl flex items-center gap-2"><BrainCircuit className="h-6 w-6 text-primary" />Tedarikçi Kod Hafızası</DialogTitle>
          <p className="text-sm text-muted-foreground">Onaylanan her faturada tedarikçinin ürün kodu → stok ürünü eşleşmesi öğrenilir. Sonraki faturalarda aynı kod <b>%100</b> otomatik eşleşir.</p>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Kod, ürün veya tedarikçi ara..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="supplier-codes-search" />
        </div>
        {rows === null ? <div className="py-10 flex justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
          : rows.length === 0 ? <p className="py-10 text-center text-sm text-muted-foreground" data-testid="supplier-codes-empty">Henüz öğrenilmiş kod yok. Onay kuyruğundan bir fatura onaylayınca eşleşmeler burada birikir.</p>
          : (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead><tr className="bg-muted/50 text-[10px] uppercase tracking-wider text-muted-foreground"><th className="p-2 text-left">Tedarikçi</th><th className="p-2 text-left">Tedarikçi Kodu</th><th className="p-2 text-left">Stok Ürünü</th><th className="p-2 text-right">Kullanım</th><th className="p-2 text-left">Son</th><th className="p-2 w-10"></th></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t" data-testid={`supplier-code-${r.id}`}>
                      <td className="p-2 font-semibold truncate max-w-[200px]" title={r.supplier_title}>{r.supplier_title}</td>
                      <td className="p-2 font-mono text-xs font-bold">{r.code}<div className="font-sans font-normal text-muted-foreground truncate max-w-[220px]" title={r.item_name}>{r.item_name}</div></td>
                      <td className="p-2 truncate max-w-[240px]" title={r.product_name}>{r.product_name}</td>
                      <td className="p-2 text-right tabular-nums">{r.times_used || 1}×</td>
                      <td className="p-2 text-xs text-muted-foreground tabular-nums">{fmtDate(r.last_used)}</td>
                      <td className="p-2"><Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => del(r)} data-testid={`supplier-code-del-${r.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
}
